/**
 * ClickSense → PostHog adapter.
 *
 * Usage:
 *   import { createPostHogAdapter } from 'clicksense/adapters/posthog';
 *   import { ClickSense } from 'clicksense';
 *
 *   new ClickSense({
 *     onCapture: createPostHogAdapter(),       // uses window.posthog
 *     onCapture: createPostHogAdapter(posthog), // explicit instance
 *     onCapture: createPostHogAdapter(posthog, 'click_confidence', { runtime: 'electron' }),
 *   });
 */

// Version stamp injected at build time via esbuild `define` (see build.js).
// The typeof guard keeps the un-built `src` import path working (typeof on an
// undeclared identifier is safe in JS — returns 'undefined'). Fallback version
// MUST equal the current package.json version.
const CLICKSENSE_VERSION = (typeof __CLICKSENSE_VERSION__ !== 'undefined') ? __CLICKSENSE_VERSION__ : '0.2.0';
const CLICKSENSE_BUILD = (typeof __CLICKSENSE_BUILD__ !== 'undefined') ? __CLICKSENSE_BUILD__ : 'dev';

/**
 * @param {object} [posthogInstance] - PostHog instance. Falls back to window.posthog.
 * @param {string} [eventName='click_confidence'] - PostHog event name.
 * @param {object} [extraProps] - Additional properties merged into every event (e.g. { runtime: 'electron' }).
 * @returns {function} onCapture callback for ClickSense
 */
export function createPostHogAdapter(posthogInstance, eventName = 'click_confidence', extraProps) {
  return function onCapture(event) {
    const ph = posthogInstance || window.posthog;

    if (!ph || typeof ph.capture !== 'function') {
      console.warn('ClickSense PostHog adapter: posthog not available');
      return;
    }

    // Flatten target into top-level properties for easier PostHog filtering
    const props = {
      duration_ms: event.duration_ms,
      click_x: event.x,
      click_y: event.y,
      drag_distance: event.drag_distance,
      input_type: event.input_type,
      target_tag: event.target.tag,
    };

    if (event.target.id) props.target_id = event.target.id;
    if (event.target.label) props.target_label = event.target.label;
    if (event.target.classes) props.target_classes = event.target.classes;
    if (event.target.href) props.target_href = event.target.href;
    if (event.target.text) props.target_text = event.target.text;

    // Auto-label fields (when clicksense has autoLabel enabled)
    if (event.target.aria_label) props.target_aria_label = event.target.aria_label;
    if (event.target.title) props.target_title = event.target.title;
    if (event.target.name) props.target_name_attr = event.target.name;
    if (event.target.value) props.target_value = event.target.value;
    if (event.target.placeholder) props.target_placeholder = event.target.placeholder;
    if (event.target.name_computed) props.target_name = event.target.name_computed;
    if (event.target.path) props.target_path = event.target.path;

    // data-* attributes: flatten as target_data_<key>
    if (event.target.data) {
      for (const key in event.target.data) {
        // PostHog property names must be simple; keep this conservative.
        const clean = key.replace(/[^a-zA-Z0-9_-]/g, '_');
        props['target_data_' + clean] = event.target.data[key];
      }
    }

    // Flatten approach dynamics into top-level properties for PostHog filtering.
    // These are only present when enableApproachDynamics is true.
    if (event.approach) {
      props.approach_velocity_mean = event.approach.approach_velocity_mean;
      props.approach_velocity_final = event.approach.approach_velocity_final;
      props.approach_deceleration = event.approach.approach_deceleration;
      props.approach_corrections = event.approach.approach_corrections;
      props.approach_distance = event.approach.approach_distance;
      props.approach_pause_ms = event.approach.approach_pause_ms;
      props.approach_linearity = event.approach.approach_linearity;
      props.approach_max_deviation = event.approach.approach_max_deviation;
      props.approach_trajectory_type = event.approach.approach_trajectory_type;
    }

    // Version-stamp every event so bundle-version skew across embedding sites
    // is queryable directly in the cs_click table. Added unconditionally;
    // extraProps below can still override if a site needs to.
    props.clicksense_version = CLICKSENSE_VERSION;
    props.clicksense_build = CLICKSENSE_BUILD;

    if (extraProps) Object.assign(props, extraProps);

    ph.capture(eventName, props);
  };
}
